import React, { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import "@fullcalendar/daygrid/main.css";
import moment from "moment";
import { CircularProgress } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import { useParams, useSearchParams } from "react-router-dom";
import { memberDetailApi, memberAllDetailApi } from "src/DAL/member/member";
import { programme_detail_api } from "src/DAL/Programme/Programme";
import GeneralCalendarClient from "src/components/GeneralComponents/GeneralCalendarClient";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

const get_view_dates = (calendarValue, referenceDate) => {
  let tool = calendarValue;
  const base = referenceDate ? moment(referenceDate) : moment();
  const startOfMonth = base.clone().startOf(tool);
  const daysToSubtract = startOfMonth.day() - 0;
  const previousSunday = startOfMonth.subtract(daysToSubtract, "days");
  let previousSundayDate = previousSunday.toDate();
  let add_days = 42;
  if (tool == "week") {
    add_days = 6;
  } else if (tool == "day") {
    add_days = 0;
  }
  let nextSaturday = startOfMonth.add(add_days, "days");
  let nextSaturdayDate = nextSaturday.toDate();
  if (tool == "day") {
    previousSundayDate = referenceDate ? new Date(referenceDate) : new Date();
    nextSaturdayDate = referenceDate ? new Date(referenceDate) : new Date();
  }

  return {
    start_date: previousSundayDate,
    end_date: nextSaturdayDate,
  };
};

function MemberCalendar() {
  const { id, CalId } = useParams();
  const [searchParams] = useSearchParams();
  const calendarRef = useRef();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [isPasted, setIsPasted] = useState(false);
  const classes = useStyles();

  const urlDate = searchParams.get("date");
  const isValidUrlDate =
    urlDate && moment(urlDate, "YYYY-MM-DD", true).isValid();
  const [currentDate, setCurrentDate] = useState(
    get_view_dates("month", isValidUrlDate ? urlDate : undefined),
  );
  // Stable string key for queryKey to prevent unnecessary refetches
  const currentDateKey = `${moment(currentDate.start_date).format(
    "YYYY-MM-DD",
  )}_${moment(currentDate.end_date).format("YYYY-MM-DD")}`;
  const [manualLoading, setManualLoading] = useState(false);
  const [programmeData, setProgrammeData] = useState(null);
  const reloadRequestRef = useRef({
    id: null,
    message: null,
    skipScroll: false,
    scrollY: 0,
    calScrolls: null,
  });

  useEffect(() => {
    const fetchProgrammeDetails = async () => {
      if (CalId) {
        const result = await programme_detail_api(CalId);

        if (result.code === 200) {
          setProgrammeData(result.program);
        } else {
          enqueueSnackbar(result.message, { variant: "error" });
        }
      }
    };
    fetchProgrammeDetails();
  }, [CalId, enqueueSnackbar]);

  const goToToday = () => {
    if (calendarRef?.current) {
      const calendarApi = calendarRef.current.getApi();
      const today = new Date();
      const viewStart = calendarApi.view.currentStart;
      const viewMonth = viewStart.getMonth();
      const viewYear = viewStart.getFullYear();
      const todayMonth = today.getMonth();
      const todayYear = today.getFullYear();
      const isSameMonth = viewMonth === todayMonth && viewYear === todayYear;

      if (!isSameMonth) {
        calendarApi.gotoDate(today);
        const newDateRange = {
          start_date: calendarApi.view.activeStart,
          end_date: calendarApi.view.activeEnd,
        };
        setCurrentDate(newDateRange);
      } else {
        const todayDate = today.toISOString().slice(0, 10);
        const todayCell = document.querySelector(
          `.fc-day[data-date="${todayDate}"]`,
        );
        if (todayCell) {
          todayCell.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
  };

  const handleScrollCalled = (data) => {
    const todayCell = document.querySelector(
      `.calendar-workout-card[id="${data}"]`,
    );
    if (todayCell)
      todayCell.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const getupdatedData = (workouts) => {
    return workouts.map((workout) => ({
      ...workout,
      exercises: workout.exercises?.map((exercise) => ({
        ...exercise,
        sets: exercise.sets?.map((set) => ({
          ...set,
          vdot: null,
          pace_low: 0,
          pace_up: 423,
        })),
        sub_exercises: exercise.sub_exercises?.map((subExercise) => ({
          ...subExercise,
          sets: subExercise.sets?.map((set) => ({
            ...set,
            vdot: null,
            pace_low: 0,
            pace_up: 423,
          })),
        })),
      })),
    }));
  };

  const prefetchAdjacentMonths = async () => {
    if (!calendarRef?.current) return;

    const calendarApi = calendarRef.current.getApi();
    const originalDate = calendarApi.getDate();

    // Calculate next/prev month dates without navigating the calendar
    const nextMonthStart = moment(originalDate)
      .add(1, "month")
      .startOf("month");
    const prevMonthStart = moment(originalDate)
      .subtract(1, "month")
      .startOf("month");

    const nextRange = get_view_dates("month", nextMonthStart.toISOString());
    const prevRange = get_view_dates("month", prevMonthStart.toISOString());

    const nextPostData = {
      start_date: moment(nextRange.start_date).format("YYYY-MM-DD"),
      end_date: moment(nextRange.end_date).format("YYYY-MM-DD"),
      program: CalId,
    };

    const prevPostData = {
      start_date: moment(prevRange.start_date).format("YYYY-MM-DD"),
      end_date: moment(prevRange.end_date).format("YYYY-MM-DD"),
      program: CalId,
    };

    const nextDateKey = `${nextPostData.start_date}_${nextPostData.end_date}`;
    const prevDateKey = `${prevPostData.start_date}_${prevPostData.end_date}`;

    try {
      await Promise.all([
        queryClient.prefetchQuery({
          queryKey: ["memberCalendarEvents", id, CalId, nextDateKey],
          queryFn: async () => {
            const result = await memberDetailApi(id, nextPostData);
            if (result.code === 200) {
              const mergedData = [
                ...result?.workouts,
                ...result?.program_workouts.filter(
                  (item) =>
                    !result?.workouts.find(
                      (existingItem) => existingItem.workout_id === item._id,
                    ),
                ),
              ];
              const processedData = getupdatedData(mergedData);
              return {
                workouts: processedData,
                reorderWorkouts: result?.reordered_workouts,
                memberInfo: result?.member_info,
                defaultTimeZone: "asia/karachi",
              };
            }
            return [];
          },
        }),
        queryClient.prefetchQuery({
          queryKey: ["memberCalendarEvents", id, CalId, prevDateKey],
          queryFn: async () => {
            const result = await memberDetailApi(id, prevPostData);
            if (result.code === 200) {
              const mergedData = [
                ...result?.workouts,
                ...result?.program_workouts.filter(
                  (item) =>
                    !result?.workouts.find(
                      (existingItem) => existingItem.workout_id === item._id,
                    ),
                ),
              ];
              const processedData = getupdatedData(mergedData);
              return {
                workouts: processedData,
                reorderWorkouts: result?.reordered_workouts,
                memberInfo: result?.member_info,
                defaultTimeZone: "asia/karachi",
              };
            }
            return [];
          },
        }),
      ]);
    } catch (error) {
      console.info("Prefetch error:", error);
    }
  };

  const { data: memberData, isLoading: isMemberLoading } = useQuery({
    queryKey: ["memberDetail", id],
    keepPreviousData: true,
    queryFn: async () => {
      const result = await memberAllDetailApi(id);
      if (result.code == 200) {
        localStorage.setItem(
          "day_calories",
          JSON.stringify({
            low_day_calories: result?.member?.low_day_calories,
            high_day_calories: result?.member?.high_day_calories,
            regular_day_calories: result?.member?.regular_day_calories,
            deficit_percentage: result?.member?.deficit_percentage,
            deficit_value: result?.member?.deficit_value,
            kcal_type: result?.member?.kcal_type,
            high_day_carb: result?.member?.high_day_carb,
            high_day_fat: result?.member?.high_day_fat,
            high_day_protein: result?.member?.high_day_protein,
            low_day_carb: result?.member?.low_day_carb,
            low_day_fat: result?.member?.low_day_fat,
            low_day_protein: result?.member?.low_day_protein,
            gender: result?.member?.gender || "",
            is_menstrual_cycle: result?.member?.is_menstrual_cycle || "",
            permission_cycle: result?.member?.permission_cycle || "",
          }),
        );
        return result;
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
        throw new Error(result.message);
      }
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const memberAllDetails = memberData?.member;

  const {
    data: eventData,
    isLoading: isEventLoading,
    refetch: refetchEvents,
  } = useQuery({
    queryKey: ["memberCalendarEvents", id, CalId, currentDateKey],
    keepPreviousData: true,
    queryFn: async () => {
      let postData = {
        start_date: moment(currentDate.start_date).format("YYYY-MM-DD"),
        end_date: moment(currentDate.end_date).format("YYYY-MM-DD"),
        program: CalId,
      };
      setManualLoading(true);
      const result = await memberDetailApi(id, postData);
      if (result.code === 200) {
        setManualLoading(false);

        const mergedData = [
          ...result?.workouts,
          ...result?.program_workouts.filter(
            (item) =>
              !result?.workouts.find(
                (existingItem) => existingItem.workout_id === item._id,
              ),
          ),
        ];

        const processedData = getupdatedData(mergedData);
        return {
          workouts: processedData,
          reorderWorkouts: result?.reordered_workouts,
          memberInfo: result?.member_info,
          defaultTimeZone: "asia/karachi",
        };
      } else {
        setIsPasted(false);
        setManualLoading(false);
        throw new Error(result.message);
      }
    },
    onSuccess: () => {
      prefetchAdjacentMonths();
      setIsPasted(false);
      const { id, message, skipScroll } = reloadRequestRef.current;
      if (message) {
        enqueueSnackbar(message, { variant: "success" });
      }

      if (!skipScroll) {
        if (id) {
          setTimeout(() => handleScrollCalled(id), 100);
        } else {
          // setTimeout(() => {
          //   const today = new Date();
          //   const todayDate = today.toISOString().slice(0, 10);
          //   const todayCell = document.querySelector(
          //     `.fc-day[data-date="${todayDate}"]`,
          //   );
          //   if (todayCell) {
          //     todayCell.scrollIntoView({ behavior: "smooth", block: "center" });
          //   }
          // }, 100);
        }
      } else {
        const savedScrollY = reloadRequestRef.current.scrollY;
        const savedCalScrolls = reloadRequestRef.current.calScrolls;
        requestAnimationFrame(() => {
          window.scrollTo({ top: savedScrollY, behavior: "instant" });
          if (savedCalScrolls) {
            const scrollers = document.querySelectorAll(".fc-scroller");
            savedCalScrolls.forEach(({ index, top, left }) => {
              const el = scrollers[index];
              if (el) {
                el.scrollTop = top;
                el.scrollLeft = left;
              }
            });
          }
        });
      }
      reloadRequestRef.current = {
        id: null,
        message: null,
        skipScroll: false,
        scrollY: 0,
        calScrolls: null,
      };
    },
    onError: (error) => {
      setIsPasted(false);
      setManualLoading(false);
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const workouts = eventData?.workouts || [];
  const reorderWorkouts = eventData?.reorderWorkouts || [];
  const memberInfo = eventData?.memberInfo || {};
  const defaultTimeZone = eventData?.defaultTimeZone || "asia/karachi";

  const getEventListing = async (
    reloadId,
    message,
    setOpenCopyPasteConfig,
    skipScroll = false,
  ) => {
    if (setOpenCopyPasteConfig) {
      setOpenCopyPasteConfig(false);
    }

    let calScrolls = null;
    if (skipScroll) {
      const scrollers = document.querySelectorAll(".fc-scroller");
      calScrolls = Array.from(scrollers).map((el, i) => ({
        index: i,
        top: el.scrollTop,
        left: el.scrollLeft,
      }));
    }

    reloadRequestRef.current = {
      id: reloadId,
      message: message,
      skipScroll: skipScroll,
      scrollY: skipScroll ? window.scrollY : 0,
      calScrolls,
    };
    refetchEvents();
  };

  const isLoading = isMemberLoading || isEventLoading;

  if (isLoading) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <>
      <GeneralCalendarClient
        programWorkoutList={workouts}
        reorderedWorkoutList={reorderWorkouts}
        setCurrentDate={setCurrentDate}
        showAddEvent={true}
        time_zone={defaultTimeZone}
        currentDate={currentDate}
        isLoading={isLoading}
        setIsLoading={() => {}}
        reloadList={getEventListing}
        calendar_title={
          programmeData?.title ? programmeData?.title + " Calendar" : "Calendar"
        }
        programme={programmeData}
        member_id={id}
        member_vdot={
          memberAllDetails?.pace_calculator
            ? memberAllDetails?.vdot?.vdot_value
            : null
        }
        member_vdot_info={
          memberAllDetails?.pace_calculator ? memberAllDetails?.vdot : null
        }
        type_member={true}
        isPasted={isPasted}
        setIsPasted={setIsPasted}
        show_back_button={true}
        calendarRef={calendarRef}
        goToToday={goToToday}
        memberInfo={memberInfo}
        manualLoading={manualLoading}
        initialDate={isValidUrlDate ? urlDate : undefined}
      />
    </>
  );
}

export default MemberCalendar;
